
import React from "react";
import { Helmet } from "react-helmet";
import Layout from "@/components/layout/Layout";

export default function PrivacyPolicy() {
  return (
    <Layout>
      <Helmet>
        <title>Privacy Policy | Grandpal</title>
        <meta name="description" content="Privacy Policy for Grandpal - How we collect, use, and protect your personal information." />
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="container py-12 max-w-4xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold text-grandpal-green mb-6">Privacy Policy</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-lg mb-6">Last Updated: April 16, 2025</p>
          
          <h2>1. Introduction</h2>
          <p>
            Grandpal ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services related to finding nursing home care in Ireland.
          </p>
          <p>
            By using our service, you consent to the data practices described in this policy. We may change this policy from time to time, and if we do, we'll post any changes on this page.
          </p>
          
          <h2>2. Information We Collect</h2>
          <p>We collect the following types of information:</p>
          <h3>2.1. Personal Information</h3>
          <ul>
            <li><strong>Contact information:</strong> Name, email address, phone number</li>
            <li><strong>Relationship information:</strong> Your relationship to the person requiring care</li>
            <li><strong>Care requirements:</strong> Information about care needs, mobility requirements, and medical conditions</li>
            <li><strong>Resident details:</strong> The salutation (Mr., Ms., Mrs.) and last name of the person requiring care, and their age</li>
            <li><strong>Location preferences:</strong> Counties and specific nursing homes of interest</li>
            <li><strong>Financial information:</strong> Fair Deal Scheme status and related information</li>
            <li><strong>Timeline information:</strong> Urgency and timeframe for finding care</li>
          </ul>
          
          <h3>2.2. Usage Data</h3>
          <p>
            We may also collect information about how you access and use our website, including your browser type, the pages you visited, the time and date of your visit, and other analytical data to improve our service.
          </p>
          
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect for the following purposes:</p>
          <ul>
            <li><strong>To provide our service:</strong> We use your information to match you with appropriate nursing homes and facilitate introductions.</li>
            <li><strong>To communicate with you:</strong> We may contact you regarding your inquiries, updates on nursing home availability, or follow-up on your care needs.</li>
            <li><strong>To share with relevant nursing homes:</strong> With your explicit consent, we share your information with nursing homes that match your requirements to facilitate introductions and placement.</li>
            <li><strong>To improve our services:</strong> We use feedback and usage data to enhance and optimize our platform.</li>
            <li><strong>For legal compliance:</strong> We process your data as required by applicable laws and regulations.</li>
          </ul>
          
          <h2>4. Sharing Your Information</h2>
          <p>
            We may share your personal information in the following circumstances:
          </p>
          <ul>
            <li><strong>Nursing Home Partners:</strong> With your explicit consent, we share your contact details and care requirements with nursing homes that match your criteria. This is done solely to facilitate introductions and potential placement.</li>
            <li><strong>Service Providers:</strong> We may engage trusted third parties to perform services on our behalf, such as hosting, data analysis, and customer service. These providers have access to your personal information only to perform these tasks and are obligated to maintain its confidentiality.</li>
            <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, such as to comply with a court order, legal process, or government request.</li>
          </ul>
          
          <h2>5. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. We will securely delete your personal information when it is no longer needed for these purposes.
          </p>
          
          <h2>6. Your Data Protection Rights</h2>
          <p>
            Under the General Data Protection Regulation (GDPR) and other applicable data protection laws, you have certain rights regarding your personal data, including:
          </p>
          <ul>
            <li><strong>Right to Access:</strong> You can request copies of your personal data.</li>
            <li><strong>Right to Rectification:</strong> You can request that we correct inaccurate or complete incomplete information.</li>
            <li><strong>Right to Erasure:</strong> You can request that we delete your personal data in certain circumstances.</li>
            <li><strong>Right to Restrict Processing:</strong> You can request that we limit the processing of your data.</li>
            <li><strong>Right to Data Portability:</strong> You can request to receive your data in a structured, machine-readable format.</li>
            <li><strong>Right to Object:</strong> You can object to our processing of your personal data in certain circumstances.</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the details provided in the "Contact Us" section below.
          </p>
          
          <h2>7. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, so we cannot guarantee absolute security.
          </p>
          
          <h2>8. Children's Privacy</h2>
          <p>
            Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected personal information from a child under 18, we will take steps to delete that information.
          </p>
          
          <h2>9. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these third-party sites. We encourage you to read the privacy policies of any third-party sites you visit.
          </p>
          
          <h2>10. International Data Transfers</h2>
          <p>
            Your personal information may be transferred to and processed in countries other than the country in which you are resident. These countries may have data protection laws that are different from the laws of your country. By using our services, you consent to the transfer of your information to these countries. We take appropriate measures to ensure your data is protected in accordance with this Privacy Policy.
          </p>
          
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
          </p>
          
          <h2>12. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <p>
            Email: <a href="mailto:privacy@grandpal.ie" className="text-grandpal-green">privacy@grandpal.ie</a><br />
            Phone: <a href="tel:+35315413976" className="text-grandpal-green">+353 1 541 3976</a><br />
            Address: Grandpal, 123 Main Street, Dublin 2, Ireland
          </p>
        </div>
      </div>
    </Layout>
  );
}
